/**
 * PersonSelect — reusable searchable dropdown for patients, nurses, and doctors.
 *
 * Usage:
 *   <PersonSelect role="patient"  value={form.patient_id}  onChange={id => setForm(f => ({...f, patient_id: id}))} />
 *   <PersonSelect role="nurse"    value={form.nurse_id}    onChange={id => setForm(f => ({...f, nurse_id: id}))} />
 *   <PersonSelect role="doctor"   value={form.doctor_id}   onChange={id => setForm(f => ({...f, doctor_id: id}))} />
 *
 * Props:
 *   role        'patient' | 'nurse' | 'doctor'  (required)
 *   value       currently selected party_id
 *   onChange    (party_id: string) => void
 *   placeholder optional override for the blank option label
 *   required    boolean
 *   disabled    boolean
 *   style       style object applied to the <select>
 *   filter      (person) => boolean  — optional client-side filter
 */
import React, { useEffect, useState } from 'react';
import { patientAPI, nurseAPI, doctorAPI } from '../services/api';

const APIS = {
  patient: patientAPI.list,
  nurse:   nurseAPI.list,
  doctor:  doctorAPI.list,
};

const LABELS = {
  patient: 'Patient',
  nurse:   'Nurse',
  doctor:  'Doctor',
};

const selectStyle = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #ece3e7',
  borderRadius: 8,
  fontSize: 13,
  boxSizing: 'border-box',
  fontFamily: 'Inter, sans-serif',
  background: '#fff',
  appearance: 'auto',
};

export default function PersonSelect({
  role,
  value = '',
  onChange,
  placeholder,
  required = false,
  disabled = false,
  style = {},
}) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFn = APIS[role];
    if (!fetchFn) { setLoading(false); return; }
    setLoading(true);
    fetchFn({ per_page: 500 })
      .then(res => {
        const list = res?.data?.data || res?.data || [];
        setPeople(Array.isArray(list) ? list : []);
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  }, [role]);

  const label = LABELS[role] || 'Person';
  const blankLabel = loading
    ? `Loading ${label}s…`
    : error
    ? `Error loading ${label}s`
    : placeholder || `— Select ${label} —`;

  return (
    <select
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      required={required}
      disabled={disabled || loading}
      style={{ ...selectStyle, ...style, opacity: (disabled || loading) ? 0.6 : 1 }}
    >
      <option value="">{blankLabel}</option>
      {people.map(p => (
        <option key={p.party_id} value={p.party_id}>
          {p.name || p.display_name || p.party_id}
          {p.specialization ? ` (${p.specialization})` : ''}
          {p.license_number ? ` · ${p.license_number}` : ''}
        </option>
      ))}
    </select>
  );
}
